/**
 * Riester Fonts API Client
 * Centralized HTTP client with JWT auth for all backend calls.
 */
import type {
  FontFamily, FontDetail, FontListParams, FontListResult, AlphabetResult,
  Collection, CollectionsListResult, Tag, TagsListResult, User,
  ToggleFavoriteResult, ActivationToggleResult, FontRatingResult, StatsDashboard,
  BrandKit, DuplicateCheckResult, FamilyDuplicatesResult, FontHealthSummary,
  AddTagsResult, SimilarFontsResult, CatalogListResult, SmartSearchResult,
  PairingsResult, LicenseSummaryResult, CatalogImportResult,
  AdminUser, AdminUsersListResult, PaginatedActivity,
  ActivationsListResult, ActivationRow,
} from '../types/index.js';

// In dev, Vite proxies /api → localhost:3001 (no CORS needed)
// In production, set VITE_API_URL to your API server
const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  status?: number;
  data?: unknown;

  constructor(message: string, status?: number, data?: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// ─── Token Management ──────────────────────────────────

let token: string | null = localStorage.getItem('fontvault_token');
let refreshToken: string | null = localStorage.getItem('fontvault_refresh_token');
let onAuthError: (() => void) | null = null; // callback for 401s (logout)
let refreshPromise: Promise<boolean> | null = null; // dedup concurrent refresh attempts

export function setToken(t: string | null) {
  token = t;
  if (t) {
    localStorage.setItem('fontvault_token', t);
  } else {
    localStorage.removeItem('fontvault_token');
  }
}

export function setRefreshToken(t: string | null) {
  refreshToken = t;
  if (t) {
    localStorage.setItem('fontvault_refresh_token', t);
  } else {
    localStorage.removeItem('fontvault_refresh_token');
  }
}

export function getToken() {
  return token;
}

export function setOnAuthError(callback: () => void) {
  onAuthError = callback;
}

// ─── Token Refresh ────────────────────────────────────

async function attemptRefresh(): Promise<boolean> {
  if (!refreshToken) return false;

  // Dedup: if a refresh is already in flight, wait for it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ─── Base Fetch ────────────────────────────────────────

async function request<T = unknown>(path: string, options: RequestInit = {}, _isRetry = false): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = { ...(options.headers as Record<string, string> | undefined) };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 — try refresh once before giving up
  if (res.status === 401 && !_isRetry) {
    const data = await res.json().catch(() => ({}));
    if (data.code === 'TOKEN_EXPIRED' || token) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return request<T>(path, options, true);
      }
    }
    setToken(null);
    setRefreshToken(null);
    onAuthError?.();
    throw new ApiError('Session expired', 401);
  }

  if (res.status === 401) {
    setToken(null);
    setRefreshToken(null);
    onAuthError?.();
    throw new ApiError('Session expired', 401);
  }

  // Parse response
  let data: any;
  const contentType = res.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.blob();
  }

  if (!res.ok) {
    throw new ApiError(data?.error || `Request failed (${res.status})`, res.status, data);
  }

  return data as T;
}

function get<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T> {
  const query = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
  return request<T>(`${path}${query}`);
}

function post<T = unknown>(path: string, body?: unknown): Promise<T> {
  if (body instanceof FormData) {
    return request<T>(path, { method: 'POST', body });
  }
  return request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined });
}

function put<T = unknown>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined });
}

function del<T = unknown>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

// ─── Auth ──────────────────────────────────────────────

export const auth = {
  login: (email: string, password: string) => post<{ token: string; refreshToken: string; user: User }>('/auth/login', { email, password }),
  me: () => get<User>('/auth/me'),
};

// ─── Fonts ─────────────────────────────────────────────

export const fonts = {
  list: (params: FontListParams = {}) => get<FontListResult>('/fonts', params as Record<string, unknown>),

  get: (id: number) => get<FontDetail>(`/fonts/${id}`),

  upload: (files: File[], collectionId?: number, licenseFiles?: File[], options: { replaceIds?: number[] } = {}) => {
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    for (const file of (licenseFiles || [])) formData.append('license_files', file);
    if (collectionId) formData.append('collectionId', String(collectionId));
    if (options.replaceIds?.length) formData.append('replaceIds', JSON.stringify(options.replaceIds));
    return post('/fonts/upload', formData);
  },

  checkDuplicates: (fileList: { name: string; hash: string }[]) =>
    post<DuplicateCheckResult>('/fonts/check-duplicates', { files: fileList }),

  delete: (id: number) => del(`/fonts/${id}`),

  toggleFavorite: (id: number) => put<ToggleFavoriteResult>(`/fonts/${id}/favorite`),

  getDownloadUrl: (fontId: number, fileId: number) =>
    `${API_BASE}/fonts/${fontId}/download/${fileId}`,

  serveUrl: (fontId: number, fileId: number) =>
    `${API_BASE}/fonts/${fontId}/serve/${fileId}`,

  downloadLicensePdf: async (fontId: number) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/fonts/${fontId}/license-pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('License PDF not available');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'license.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },

  alphabet: () => get<AlphabetResult>('/fonts/alphabet'),

  bulkDelete: (fontIds: number[]) => post<{ deleted: number }>('/fonts/bulk-delete', { fontIds }),

  bulkTag: (fontIds: number[], tagIds: number[]) => post<{ added: number }>('/fonts/bulk-tag', { fontIds, tagIds }),

  addTags: (fontId: number, tagIds: number[]) => post<AddTagsResult>(`/fonts/${fontId}/tags`, { tagIds }),

  removeTag: (fontId: number, tagId: number) => del(`/fonts/${fontId}/tags/${tagId}`),

  similar: (fontId: number) => get<SimilarFontsResult>(`/fonts/${fontId}/similar`),

  rate: (fontId: number, rating: number) => put<FontRatingResult>(`/fonts/${fontId}/rate`, { rating }),

  removeRating: (fontId: number) => del<FontRatingResult>(`/fonts/${fontId}/rate`),

  duplicates: () => get<FamilyDuplicatesResult>('/fonts/duplicates'),

  health: () => get<FontHealthSummary>('/fonts/health'),

  healthScan: (fontIds?: number[]) => post<{ scanned: number; results: unknown[] }>('/fonts/health/scan', { fontIds }),

  collect: async (fontIds: number[]) => {
    const url = `${API_BASE}/fonts/collect`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fontIds }),
    });
    if (!res.ok) throw new ApiError('ZIP export failed', res.status);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'fonts-collection.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },
};

// ─── Tags ─────────────────────────────────────────────

export const tags = {
  list: () => get<TagsListResult>('/tags'),
  create: (data: { name: string; color?: string }) => post<Tag>('/tags', data),
  delete: (id: number) => del(`/tags/${id}`),
};

// ─── Collections ───────────────────────────────────────

export const collections = {
  list: () => get<CollectionsListResult>('/collections'),
  create: (data: { name: string; description?: string; icon?: string; parent_id?: number }) => post<Collection>('/collections', data),
  update: (id: number, data: { name?: string; description?: string | null; icon?: string; parent_id?: number | null }) => put<Collection>(`/collections/${id}`, data),
  delete: (id: number) => del(`/collections/${id}`),
  addFonts: (id: number, fontIds: number[]) => post<{ added: number }>(`/collections/${id}/fonts`, { fontIds }),
  removeFont: (collectionId: number, fontId: number) => del(`/collections/${collectionId}/fonts/${fontId}`),
  fontIds: (id: number) => get<{ fontIds: number[] }>(`/collections/${id}/font-ids`),
};

// ─── Google Fonts ─────────────────────────────────────

export const googlefonts = {
  list: (params: Record<string, unknown> = {}) => get<CatalogListResult>('/googlefonts', params),
  import: (data: { family: string; category?: string; variants?: string[] }) => post<CatalogImportResult>('/googlefonts/import', data),
};

// ─── Adobe Fonts ──────────────────────────────────────

export const adobefonts = {
  list: (params: Record<string, unknown> = {}) => get<CatalogListResult>('/adobefonts', params),
  import: (data: { family: string; category?: string; styleCount?: number }) => post<CatalogImportResult>('/adobefonts/import', data),
};

// ─── AI ──────────────────────────────────────────────

export const ai = {
  smartSearch: (query: string) => post<SmartSearchResult>('/ai/smart-search', { query }),
  pairings: (fontId: number) => get<PairingsResult>(`/ai/pairings/${fontId}`),
  licenseSummary: (fontId: number) => get<LicenseSummaryResult>(`/ai/license-summary/${fontId}`),
};

// ─── Activations ───────────────────────────────────────

export const activations = {
  list: () => get<ActivationsListResult>('/activations'),
  toggle: (fontId: number, active?: boolean, deviceId?: string) =>
    put<ActivationToggleResult>(`/activations/${fontId}`, { active, deviceId }),
  bulk: (fontIds: number[], active?: boolean, source?: string, sourceId?: string) =>
    post<{ updated: number }>('/activations/bulk', { fontIds, active, source, sourceId }),
  sync: (deviceId?: string, currentFontIds?: number[], lastSyncAt?: string) =>
    post<{ activations: ActivationRow[]; toInstall: number[]; toRemove: number[]; syncedAt: string }>(
      '/activations/sync', { deviceId, currentFontIds, lastSyncAt }
    ),
};

// ─── Users ─────────────────────────────────────────────

export const users = {
  list: (params: { all?: boolean } = {}) => get<AdminUsersListResult>('/users', params as Record<string, unknown>),
  get: (id: number) => get<AdminUser>(`/users/${id}`),
  update: (id: number, data: { name?: string; initials?: string; color?: string }) => put<User>(`/users/${id}`, data),
  updateRole: (id: number, role: string) => put<{ message: string }>(`/users/${id}/role`, { role }),
  deactivate: (id: number) => del<{ message: string }>(`/users/${id}`),
  reactivate: (id: number) => put<{ message: string }>(`/users/${id}/reactivate`),
  invite: (data: { email: string; role?: string; name?: string }) => post<{ message: string }>('/users/invite', data),
  resetPassword: (id: number, newPassword: string) => put<{ message: string }>(`/users/${id}/reset-password`, { newPassword }),
  activity: (id: number, params: { page?: number; limit?: number } = {}) => get<PaginatedActivity>(`/users/${id}/activity`, params as Record<string, unknown>),
};

// ─── Stats ─────────────────────────────────────────────

export const stats = {
  dashboard: () => get<StatsDashboard>('/stats'),
  activity: (params: { page?: number; limit?: number } = {}) => get<PaginatedActivity>('/stats/activity', params as Record<string, unknown>),
};

// ─── Health ────────────────────────────────────────────

export const health = () => get<{ status: string; timestamp?: string }>('/health');

// ─── Analytics (admin only) ───────────────────────────

export interface AnalyticsSummary {
  totals: { total_events: number; unique_users: number; views: number; actions: number };
  top_pages: { page: string; feature: string | null; count: number }[];
  top_users: { user_email: string; user_name: string | null; count: number }[];
  daily_activity: { date: string; count: number }[];
}
export interface AnalyticsEngagement {
  wau: number; mau: number; stickiness: number; avg_actions_per_user: number; new_users_30d: number;
}
export interface AnalyticsRetentionCohort {
  cohort_week: number; cohort_size: number; retained: number; retention_rate: number;
}
export interface AnalyticsFeatureEngagement {
  feature: string; views: number; actions: number; distinct_doers: number;
  top_actions: { page: string; count: number }[];
}
export interface AnalyticsUserScore {
  user_email: string; user_name: string | null; total_events: number; page_diversity: number; last_seen: string; score: number;
}
export interface AnalyticsSessions {
  total_sessions: number; avg_duration_ms: number; median_duration_ms: number;
}
export interface AnalyticsDevices {
  browsers: Record<string, number>; os: Record<string, number>; device_types: Record<string, number>;
}
export interface AnalyticsActivityRow {
  id: number; event_type: string; user_id: number | null; user_email: string;
  user_name: string | null; user_initials: string | null; page: string | null;
  feature: string | null; target: string | null; extra_data: Record<string, unknown> | null;
  created_at: string;
}
export interface AnalyticsRecentActivity {
  activities: AnalyticsActivityRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const analytics = {
  summary: (days = 30) => get<AnalyticsSummary>('/admin/analytics/summary', { days }),
  recentActivity: (params: { page?: number; limit?: number } = {}) => get<AnalyticsRecentActivity>('/admin/analytics/recent-activity', params as Record<string, unknown>),
  engagement: () => get<AnalyticsEngagement>('/admin/analytics/engagement'),
  retention: (weeks = 8) => get<AnalyticsRetentionCohort[]>('/admin/analytics/retention', { weeks }),
  featureHeatmap: (days = 14) => get<Record<string, Record<string, number>>>('/admin/analytics/feature-heatmap', { days }),
  featureEngagement: (days = 30) => get<AnalyticsFeatureEngagement[]>('/admin/analytics/feature-engagement', { days }),
  userScores: (days = 30) => get<AnalyticsUserScore[]>('/admin/analytics/user-scores', { days }),
  featureTrends: (days = 30) => get<Record<string, { date: string; count: number }[]>>('/admin/analytics/feature-trends', { days }),
  sessions: (days = 7) => get<AnalyticsSessions>('/admin/analytics/sessions', { days }),
  devices: (days = 30) => get<AnalyticsDevices>('/admin/analytics/devices', { days }),
  userActivity: (id: number, days = 30) => get<AnalyticsActivityRow[]>(`/admin/analytics/users/${id}/activity`, { days }),
};

// ─── Brand Kits ───────────────────────────────────────

export const brandkits = {
  list: () => get<{ kits: BrandKit[] }>('/brand-kits'),
  get: (id: number) => get<{ kit: BrandKit }>(`/brand-kits/${id}`),
  create: (data: { client_id: string; client_name: string; notes?: string }) => post<{ id: number; client_id: string; client_name: string }>('/brand-kits', data),
  update: (id: number, data: { client_name?: string; notes?: string }) => put<{ success: boolean }>(`/brand-kits/${id}`, data),
  remove: (id: number) => del<{ success: boolean }>(`/brand-kits/${id}`),
  addFonts: (id: number, fontIds: number[], usageNote?: string) => post<{ success: boolean }>(`/brand-kits/${id}/fonts`, { fontIds, usageNote }),
  removeFont: (id: number, fontId: number) => del<{ success: boolean }>(`/brand-kits/${id}/fonts/${fontId}`),
  updateFontNote: (id: number, fontId: number, usageNote: string) => put<{ success: boolean }>(`/brand-kits/${id}/fonts/${fontId}`, { usageNote }),
};

export default {
  auth,
  fonts,
  tags,
  collections,
  ai,
  activations,
  users,
  stats,
  health,
  googlefonts,
  adobefonts,
  brandkits,
  analytics,
  setToken,
  setRefreshToken,
  getToken,
  setOnAuthError,
};
